// listy płatności
// @ts-nocheck

import React, { useState } from 'react';
import classnames from 'classnames';
import { format } from 'date-fns';
import { Trans } from 'react-i18next';
import { ChevronRight } from 'react-bootstrap-icons';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useGetDashboardSettlements, useGetDashboardSettlementsSummary } from 'api';
import { IDashboardSettlementsListItem } from 'api/types';
import { Table, Link } from 'components/controls';
import { useSelector } from 'store';

import styles from 'theme/pages/Dashboard/Dashboard.module.scss';

const Payments = () => {
  const DASHBOARD_PAYMENTS_LIMIT = 10;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const profile = useSelector((state) => state.auth.profile);
  const canSeePayments = profile?.role === 'ROLE_USER_MAIN' || profile?.role === 'ROLE_ADMIN';

  // rodzaj aktyenej listy
  const [activeTab, setActiveTab] = useState<'upcoming' | 'expired' | 'all'>('all');

  // pobranie informacji o płatnościach
  const { data: dashboardSettlementsData } = useGetDashboardSettlements({
    enabled: canSeePayments
  });

  // pobranie podsumowania płatności
  const { data: dashboardSettlementsSummaryData } = useGetDashboardSettlementsSummary({
    enabled: canSeePayments
  });

  if (!canSeePayments) return null;

  const currentSettlements = dashboardSettlementsData?.[activeTab] || [];
  const visibleSettlements = currentSettlements.slice(0, DASHBOARD_PAYMENTS_LIMIT);
  const shouldShowMoreButton = currentSettlements.length > DASHBOARD_PAYMENTS_LIMIT;

  return (
    <div
      className={classnames(styles.listWrapper, 'StylePath-Pages-Dashboard-Components-Payments')}>
      <div className={classnames(styles.listHeader, styles.orange)}>
        <Trans>Lista płatności</Trans>
        <Link to="/dashboard/payments" className={styles.link}>
          <Trans>więcej</Trans> <ChevronRight />
        </Link>
      </div>

      <div className={styles.listTabs}>
        <div
          className={classnames(styles.tab, { [styles.active]: activeTab === 'all' })}
          onClick={() => setActiveTab('all')}>
          <Trans>Nierozliczone</Trans>
        </div>
        <div
          className={classnames(styles.tab, { [styles.active]: activeTab === 'upcoming' })}
          onClick={() => setActiveTab('upcoming')}>
          <Trans>Nadchodzące</Trans>
        </div>
        <div
          className={classnames(styles.tab, { [styles.active]: activeTab === 'expired' })}
          onClick={() => setActiveTab('expired')}>
          <Trans>Przedawnione</Trans>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        {isMobile ? (
          <div className={styles.mobileList}>
            {visibleSettlements.map((item) => (
              <div key={item.symbol} className={styles.mobileListItem}>
                <div className={styles.itemHeader}>{item.symbol}</div>
                <div className={styles.details}>
                  <div>
                    <span className={styles.label}>
                      <Trans>Data płatności</Trans>
                    </span>
                    <span>{format(new Date(item.payment_date), 'dd-MM-yyyy')}</span>
                  </div>
                  <div>
                    <span className={styles.label}>
                      <Trans>Do zapłaty</Trans>
                    </span>
                    <span>
                      {item.value_formatted} {item.currency}
                    </span>
                  </div>
                  <div>
                    <span className={styles.label}>
                      <Trans>Dni po terminie</Trans>
                    </span>
                    <span className={item.payment_diff_days === 0 ? undefined : styles.redText}>
                      {item.payment_diff_days === 0 ? '–' : item.payment_diff_days}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Table<IDashboardSettlementsListItem>
            columns={[
              {
                title: <Trans>Symbol</Trans>,
                dataIndex: 'symbol',
                align: 'left',
                renderCell: (item) => <span className="primaryText">{item.symbol}</span>
              },
              {
                title: <Trans>Data płatności</Trans>,
                dataIndex: 'payment_date',
                align: 'left',
                renderCell: (item) => (
                  <span className="grayText">
                    {format(new Date(item.payment_date), 'dd-MM-yyyy')}
                  </span>
                )
              },
              {
                title: <Trans>Do zapłaty</Trans>,
                dataIndex: 'value',
                align: 'left',
                renderCell: (item) => `${item.value_formatted} ${item.currency}`
              },
              {
                title: <Trans>Dni po terminie</Trans>,
                dataIndex: 'payment_diff_days',
                align: 'left',
                renderCell: (item) =>
                  item.payment_diff_days === 0 ? (
                    <span>–</span>
                  ) : (
                    <span className="redText">{item.payment_diff_days}</span>
                  )
              }
            ]}
            dataSource={visibleSettlements}
            rowKey="symbol"
          />
        )}
      </div>

      {shouldShowMoreButton && (
        <div className={styles.showMoreWrapper}>
          <Link to="/dashboard/payments" className={styles.showMoreLink}>
            <Trans>Pokaż więcej</Trans> <ChevronRight />
          </Link>
        </div>
      )}

      <div className={styles.listFooter}>
        <div className={styles.title}>
            <Trans>Podsumowanie płatności</Trans>
        </div>
        <div className={styles.summary}>
          <div className={styles.items}>
            <div>
              <span>
                <Trans>Lista płatności</Trans>:
              </span>{' '}
              {dashboardSettlementsSummaryData?.unsettled?.count}
            </div>
            <div>
              <span>
                <Trans>Przedawnione</Trans>:
              </span>{' '}
              {dashboardSettlementsSummaryData?.expired?.count}
            </div>
            <div>
              <span>
                <Trans>Nadchodzące</Trans>:
              </span>{' '}
              {dashboardSettlementsSummaryData?.upcoming?.count}
            </div>
          </div>
        </div>

        <div className={styles.total}>
          <div className={styles.title}>
            <Trans>Razem</Trans>:
          </div>
          <div className={styles.prices}>
            <span className={styles.gross}>
              <strong>
                {dashboardSettlementsSummaryData?.total?.value_formatted}{' '}
                {dashboardSettlementsSummaryData?.total?.currency}
              </strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payments;

